import * as React from "react";
import {useEffect, useState} from "react";
import {observer} from "mobx-react-lite";
import {useLeadStore} from "../store/Lead.store";
import {RadioButtonListControlComponent} from "../../form/radioButtonListControl.component";
import {InputControlComponent} from "../../form/inputControl.component";
import {RadioFieldListControlComponent} from "../../form/radioFieldListControl.component";
import {ReactComponent as OrientationWestSVG} from "../icons/orientation_west.svg";
import {ReactComponent as OrientationSouthWestSVG} from "../icons/orientation_south-west.svg";
import {ReactComponent as OrientationSouthSVG} from "../icons/orientation_south.svg";
import {ReactComponent as OrientationSouthEastSVG} from "../icons/orientation_south-east.svg";
import {ReactComponent as OrientationEastSVG} from "../icons/orientation_east.svg";
import {ReactComponent as OrientationEastWestSVG} from "../icons/orientation_east-west.svg";
import {ReactComponent as RoofType1SVG} from "../icons/rooftype_1.svg";
import {ReactComponent as RoofType2SVG} from "../icons/rooftype_2.svg";
import {ReactComponent as RoofType3SVG} from "../icons/rooftype_3.svg";
import {ReactComponent as RoofType4SVG} from "../icons/rooftype_4.svg";
import useBelfiusTheme from "../theme/ThemeHooks";
import {ButtonComponent} from "../../form/button.component";

const SVGS: any = {
    "orientation": {
        "west": OrientationWestSVG,
        "south-west": OrientationSouthWestSVG,
        "south": OrientationSouthSVG,
        "south-east": OrientationSouthEastSVG,
        "east": OrientationEastSVG,
        "east-west": OrientationEastWestSVG
    },
    "rooftype": {
        "1": RoofType1SVG,
        "2": RoofType2SVG,
        "3": RoofType3SVG,
        "4": RoofType4SVG,
    }
};

const RoofMaterial = observer( () => {
    const store = useLeadStore();

    const roofMaterialOptions = store.options.roof?.material?.slice()
        .map( ( option: any ) => {
            return {
                text: option.label,
                value: option.id,
                selected: store.solarSelection.roofMaterial === option.id,
                key: option.id
            };
        } );

    const roofMaterialChange = ( value: any ) => {
        store.solarSelection.setRoofMaterial( value );
    };

    return <div>
        <h2>{store.translate( 'leadgeneration.steps.solarSelectionStep.roofMaterial.title' )} *</h2>
        <RadioButtonListControlComponent name={"roofMaterial"} onChange={roofMaterialChange} options={roofMaterialOptions || []}/>
    </div>;
} );

const RoofType = observer( () => {
    const store = useLeadStore();
    let {themeClassName} = useBelfiusTheme();

    const storeOptions = store.options.roof?.type?.slice().map( ( so: any ) => {
        const Icon: any = SVGS.rooftype[so.id];
        return {
            key: so.id,
            value: so.id,
            text: so.label,
            selected: store.solarSelection.roofSlope?.toString() == so.id,
            imageUrl: <Icon/>
        }
    } );

    let additionalFields = null;

    const updateRoofSlope = ( value: any ) => {
        store.solarSelection.setRoofSlope( Number.parseInt( value ) );
    };
    const updateWidth = ( e: any ) => {
        store.solarSelection.setRoofWidth( Number.parseFloat( e.target.value ) );
    };
    const updateLength = ( e: any ) => {
        store.solarSelection.setRoofLength( Number.parseFloat( e.target.value ) );
    };

    if ( store.solarSelection.roofSlope ) {
        if ( store.solarSelection.roofSlope === 1 ) {
            // flat roof
            additionalFields = <div className='d-flex w-100'>
                <div className='col-5 p-0'>
                    <div className='form-group'>
                        <label>{store.translate( 'leadgeneration.steps.solarSelectionStep.roofType.width.label' )} *</label>
                        <InputControlComponent currentValue={store.solarSelection.roofWidth || ''}
                                               onChange={updateWidth}
                                               placeholder={'bv. 7'}
                                               type={'numeric'}
                                               theme={themeClassName}
                                               required={true}/>
                    </div>
                </div>
                <div className='col-5 offset-1 p-0'>
                    <div className='form-group'>
                        <label>{store.translate( 'leadgeneration.steps.solarSelectionStep.roofType.depth.label' )} *</label>
                        <InputControlComponent currentValue={store.solarSelection.roofLength || ''}
                                               onChange={updateLength}
                                               placeholder={'bv. 12'}
                                               type={'numeric'}
                                               theme={themeClassName}
                                               required={true}/>
                    </div>
                </div>
            </div>;
        }
        else {
            // angled roof
            additionalFields = <div className='d-flex w-100'>
                <div className='col-5 p-0'>
                    <div className='form-group'>
                        <label>{store.translate( 'leadgeneration.steps.solarSelectionStep.roofType.width.label' )} *</label>
                        <InputControlComponent currentValue={store.solarSelection.roofWidth || ''}
                                               onChange={updateWidth}
                                               theme={themeClassName}
                                               placeholder={'bv. 7'}
                                               type={'numeric'}
                                               required={true}/>
                    </div>
                </div>
                <div className='col-5 offset-1 p-0'>
                    <div className='form-group'>
                        <label>{store.translate( 'leadgeneration.steps.solarSelectionStep.roofType.angledSide.label' )} *</label>
                        <InputControlComponent currentValue={store.solarSelection.roofLength || ''}
                                               onChange={updateLength}
                                               placeholder={'bv. 9'}
                                               theme={themeClassName}
                                               type={'numeric'}
                                               required={true}/>
                    </div>
                </div>
            </div>;
        }
    }

    return <div>
        <h2>{store.translate( 'leadgeneration.steps.solarSelectionStep.roofType.title' )} *</h2>
        <div className='d-flex flex-column'>
            <div className='d-flex'>
                <RadioFieldListControlComponent name={"angle"} onChange={updateRoofSlope} theme={themeClassName} options={storeOptions || []}/>
            </div>
            <div className='d-flex'>
                {additionalFields}
            </div>
        </div>
    </div>;
} );

const RoofOrientation = observer( () => {
    const store = useLeadStore();
    let {themeClassName} = useBelfiusTheme();

    const roofOrientationOptions = store.options?.roof?.orientation.slice()
        .map( ( option: any ) => {
            const Icon: any = SVGS.orientation[option.id];
            return {
                text: option.label,
                value: option.id,
                key: option.id,
                selected: store.solarSelection?.roofOrientation === option.id,
                imageUrl: <Icon/>
            };
        } );

    const roofOrientationChange = ( value: any ) => {
        store.solarSelection.setRoofOrientation( value );
    };

    return <div>
        <h2>{store.translate( 'leadgeneration.steps.solarSelectionStep.roofOrientation.title' )} *</h2>
        <div className='d-flex flex-column'>
            <div className='d-flex'>
                <RadioFieldListControlComponent name={"roofOrientation"}
                                                onChange={roofOrientationChange}
                                                options={roofOrientationOptions || []}
                                                theme={themeClassName}
                />
            </div>
        </div>
    </div>;
} );

export const SolarSelectionStep = observer( () => {
    const store = useLeadStore();
    const [loading, setLoading] = useState( true );
    let {themeClassName} = useBelfiusTheme();

    useEffect( () => {
        if ( store ) {
            store.fetchConfigurationOptions()
                .then( () => {
                    setLoading( false );
                } )
                .catch( ( err: any ) => {
                    setLoading( false );
                    console.error( "Unexpected error whilst loading events", err )
                } )
        }
    }, [store] );

    const completeOrder = () => {
        store.updateLead().then( () => {
            store.goToNextStep();
        } )
    };

    const ProceedButton = observer( () => {
        const store = useLeadStore();
        const [nextStepRequested, setNextStepRequested] = useState( false );

        return <ButtonComponent
            disabled={!store.currentStepValid() || nextStepRequested}
            text={store.translate( 'leadgeneration.steps.solarSelectionStep.nextButton' )}
            onClick={() => {
                if ( store.currentStepValid() ) {
                    setNextStepRequested( true );
                    completeOrder();
                }
            }}
            type={'primary'}
            theme={themeClassName}
            additionalCssClasses={['w-100']}/>;
    } );

    return (<div>
        {loading && <div>Loading...</div>}
        {!loading && <React.Fragment>
            <RoofMaterial/>
            <RoofType/>
            <RoofOrientation/>
        </React.Fragment>}
        <ProceedButton/>
    </div>)
} );
